import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverFoodieWeekend() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "a-weekend-in-vancouver-for-foodies";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Saturday: Downtown Delights & Global Flavors",
    "section-2": "Sunday: Cultural Eats & West Coast Classics",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/food-and-drink/",
      label: "Destination Vancouver – Food & Drink",
    },
    {
      url: "https://www.dineoutvancouver.com",
      label: "Dine Out Vancouver Festival",
    },
    {
      url: "https://www.visitrichmondbc.com/food-and-drink/dumpling-trail/",
      label: "Tourism Richmond – Dumpling Trail",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We'll list the 10 recommended places (plus a few references for discussion).
  const locations = [
    // Day 1
    {
      id: "granville-island",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island Public Market",
    },
    {
      id: "cafe-medina",
      lat: 49.27950,
      lng: -123.11700,
      popupText: "Café Medina",
    },
    {
      id: "japadog",
      lat: 49.28224,
      lng: -123.12441,
      popupText: "Japadog (Burrard & Smithe)",
    },
    {
      id: "miku-restaurant",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "Miku Restaurant",
    },
    {
      id: "bella-gelateria",
      lat: 49.28700,
      lng: -123.11450,
      popupText: "Bella Gelateria (Coal Harbour)",
    },
    // Day 2
    {
      id: "kirin",
      lat: 49.28583,
      lng: -123.12361,
      popupText: "Kirin Seafood Restaurant (Downtown)",
    },
    {
      id: "phnom-penh",
      lat: 49.27960,
      lng: -123.09750,
      popupText: "Phnom Penh Restaurant (Chinatown)",
    },
    {
      id: "vijs",
      lat: 49.25600,
      lng: -123.11500,
      popupText: "Vij's (Cambie Street)",
    },
    {
      id: "salmon-bannock",
      lat: 49.26330,
      lng: -123.12980,
      popupText: "Salmon n’ Bannock Bistro",
    },
    {
      id: "stanley-park",
      lat: 49.30430,
      lng: -123.14430,
      popupText: "Stanley Park",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>The Ultimate Vancouver Foodie Weekend: 10 Must-Try Spots & Insider Tips</title>
        <meta
          name="description"
          content="Plan the perfect weekend in Vancouver with this foodie-focused itinerary. Discover can't-miss spots for brunch, lunch, dinner, and dessert, plus local regulations, FAQs, and tips."
        />
        <meta
          name="keywords"
          content="Vancouver, foodie, weekend, dining, travel tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/a-weekend-in-vancouver-for-foodies.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="The Ultimate Vancouver Foodie Weekend: 10 Must-Try Spots & Insider Tips"
        />
        <meta
          property="og:description"
          content="A comprehensive weekend guide to Vancouver's top foodie destinations, local rules, and insider tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/a-weekend-in-vancouver-for-foodies.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/a-weekend-in-vancouver-for-foodies.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Ultimate Vancouver Foodie Weekend: 10 Must-Try Spots & Insider Tips"
        />
        <meta
          name="twitter:description"
          content="Learn where to eat in Vancouver for the perfect weekend getaway, from dim sum to flame-seared sushi and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/a-weekend-in-vancouver-for-foodies.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A mouthwatering Vancouver dining spread."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a top foodie destination for good reason. This
              coastal metropolis boasts a world-class culinary scene that
              celebrates its multicultural influences and abundant local
              ingredients. From fresh Pacific seafood to authentic Asian fusion
              and farm-to-table bistros, the city offers something to thrill
              every palate. Get ready for an action-packed weekend guide that
              will take you through 10 must-try spots—from street eats to fine
              dining—plus insider tips to dine like a local!
            </p>

            {/* SECTION: Day 1 */}
            <h2 id="section-1">Saturday: Downtown Delights & Global Flavors</h2>
            <p>
              Kick off your Vancouver weekend in the heart of downtown. Whether
              you’re into sweet waffles or savory street food, Day 1’s itinerary
              will have you sampling world-class brunch spots, vibrant food
              markets, and high-end sushi by the waterfront.
            </p>

            {/* Cafe Medina */}
            <p>
              <strong>Café Medina</strong>{" "}
              <span
                ref={(el) => setSpotRef("cafe-medina", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27950}
                  coordX={-123.11700}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              is a beloved brunch institution famous for its Belgian waffles,
              aromatic lavender lattes, and inventive Mediterranean-inspired
              breakfast dishes. Arrive early to beat the queue; it can get
              crowded fast on weekends. Expect to spend around $20–$25 per
              person.
            </p>

            {/* Granville Island */}
            <p>
              Next, head to{" "}
              <strong>Granville Island Public Market</strong>{" "}
              <span
                ref={(el) => setSpotRef("granville-island", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27262}
                  coordX={-123.13524}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , a hub of food and culture where you’ll find an indoor market
              brimming with fresh produce, baked goods, cheese, and local
              seafood. If the weather’s nice, grab lunch from multiple vendors
              and enjoy a waterside picnic. Budget around $15–$20 for a variety
              of market bites.
            </p>

            {/* Japadog */}
            <p>
              For a quick snack on the go, try the local legend{" "}
              <strong>Japadog</strong>{" "}
              <span
                ref={(el) => setSpotRef("japadog", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28224}
                  coordX={-123.12441}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              . Their Japanese-fusion hot dogs have become an iconic Vancouver
              street food. The Terimayo dog with seaweed, teriyaki sauce, and
              mayo is a must-try. It’s a fun and budget-friendly option at about
              $7–$8.
            </p>

            {/* Miku Restaurant */}
            <p>
              Come dinnertime, treat yourself to the waterfront{" "}
              <strong>Miku Restaurant</strong>{" "}
              <span
                ref={(el) => setSpotRef("miku-restaurant", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28880}
                  coordX={-123.11125}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , renowned for its flame-seared aburi sushi—a technique that
              unlocks rich, smoky flavors. This upscale spot overlooks the
              harbor near Canada Place, perfect for a special occasion. Expect
              to spend about $50–$80 per person. Don’t skip the Salmon Oshi
              Sushi, their signature dish.
            </p>

            {/* Bella Gelateria */}
            <p>
              Wrap up the night with award-winning gelato at{" "}
              <strong>Bella Gelateria</strong>{" "}
              <span
                ref={(el) => setSpotRef("bella-gelateria", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28700}
                  coordX={-123.11450}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , another waterfront gem near the convention center. Expect a line
              on sunny weekends, but the handcrafted flavors are worth the wait.
              A single scoop is around $5–$6 and perfect for enjoying a scenic
              stroll.
            </p>

            {/* SECTION: Day 2 */}
            <h2 id="section-2">Sunday: Cultural Eats & West Coast Classics</h2>
            <p>
              Day 2 focuses on Vancouver’s diverse cultural scene—from authentic
              dim sum to modern Indian fusion. Pack your appetite for another
              delicious day.
            </p>

            {/* Kirin */}
            <p>
              Start your Sunday with a classic Vancouver tradition: dim sum at{" "}
              <strong>Kirin Seafood Restaurant (Downtown)</strong>{" "}
              <span
                ref={(el) => setSpotRef("kirin", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28583}
                  coordX={-123.12361}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              . Order shrimp dumplings, pork buns, and other Cantonese
              specialties. Expect about $25–$30 per person. Arrive early or make
              a reservation; locals flock here on weekends.
            </p>

            {/* Phnom Penh */}
            <p>
              If you’re still hungry or looking for a midday bite, head to
              Vancouver’s Chinatown and sample the award-winning Cambodian and
              Vietnamese flavors at{" "}
              <strong>Phnom Penh Restaurant</strong>{" "}
              <span
                ref={(el) => setSpotRef("phnom-penh", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27960}
                  coordX={-123.09750}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              . Their crispy chicken wings and butter beef are legendary. Expect
              a wait, but the affordable and delicious dishes make it worthwhile.
            </p>

            {/* Vij's */}
            <p>
              For dinner, experience Vancouver’s modern Indian cuisine at{" "}
              <strong>Vij’s</strong>{" "}
              <span
                ref={(el) => setSpotRef("vijs", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.25600}
                  coordX={-123.11500}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              . Their signature lamb “lollipops” in fenugreek cream curry are
              unforgettable. Expect to spend about $40–$60 per person. Arrive
              early or make limited reservations online. If you’re looking for
              something more locally inspired, consider{" "}
              <strong>Salmon n’ Bannock Bistro</strong>{" "}
              <span
                ref={(el) => setSpotRef("salmon-bannock", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.26330}
                  coordX={-123.12980}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              for an Indigenous-owned restaurant serving authentic First Nations
              dishes like wild salmon and bison.
            </p>

            {/* Stanley Park mention */}
            <p>
              Between meals, walk off the calories at{" "}
              <strong>Stanley Park</strong>{" "}
              <span
                ref={(el) => setSpotRef("stanley-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.30430}
                  coordX={-123.14430}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , Vancouver’s iconic green space with scenic seawall paths.
              Biking, strolling, or simply taking in the water views is a
              quintessential local experience.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What’s the best time of year to visit?</h3>
              <p>
                Vancouver offers fantastic food year-round. Summer (June–August)
                features sunny patios and night markets, while winter (Dec–Feb)
                hosts the Dine Out Vancouver Festival for special deals. Spring
                and autumn offer moderate crowds and seasonal delicacies like
                spot prawns and wild mushrooms.
              </p>

              <h3>2. Do I need a car to explore?</h3>
              <p>
                Not really. Downtown Vancouver is walkable, and neighborhoods
                are well connected by public transit. You can easily get around
                by SkyTrain, bus, or rideshare. Parking downtown can be
                expensive, so most visitors rely on transit or foot.
              </p>

              <h3>3. Can I find vegetarian or vegan options?</h3>
              <p>
                Absolutely! Vancouver’s diverse culinary scene caters to all
                diets. Many restaurants offer vegetarian or vegan dishes, and
                there are dedicated plant-based eateries like The Acorn or MeeT.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              • <strong>Tipping Etiquette:</strong> 15–20% on your pre-tax bill
              is standard for good service.  
              • <strong>Legal Drinking Age:</strong> 19 in British Columbia.
              Have valid ID on hand.  
              • <strong>Taxes:</strong> Expect a 5% federal tax (GST) + 7%
              provincial tax on alcohol. Menu prices usually don’t include tax.
              • <strong>Reservations:</strong> Popular spots book quickly,
              especially on weekends. Make them in advance if you can.  
              • <strong>Smoking & Cannabis:</strong> Smoking is banned indoors
              and within 6 meters of doors and windows. Cannabis is legal for
              adults 19+ but not permitted in restaurants or many public areas.  
              • <strong>COVID-19:</strong> Check official government websites
              for the latest guidelines. Most restaurants currently operate with
              no specific restrictions but policies can change.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From dim sum and sushi to fusion cuisine and local gelato, you’ll
              see why Vancouver is a paradise for food lovers. Each stop offers
              a distinct taste of the city’s diverse, multicultural flavors. Use
              this itinerary as a starting point, but feel free to explore more
              hidden gems—just bring your appetite. Enjoy your ultimate
              Vancouver foodie weekend!
            </p>

            {/* BOTTOM COMPONENT: references, author, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}